/**
 * Auth context for CTDCommons.
 *
 * Unlike the main Chrona AuthContext, this one is email/password only —
 * no Entra SSO, no Graph token capture. Signup is handled by the
 * POST /auth/signup backend route (pre-seeds an org_users row on the
 * CTDCommons public org); login uses supabase.auth.signInWithPassword
 * which produces a JWT the backend's TenantMiddleware Tier-3 path
 * activates on first hit.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { supabase } from './supabaseClient'
import { api, CommonsDeniedError } from './api'
import { Telemetry, getSessionDurationSeconds } from './telemetry'
import {
  COMMONS_AUTH_ERROR_KEY,
  COMMONS_AUTH_ERROR_MESSAGE,
} from './commonsAccess'
import type { CommonsUser } from '@/types/api'

interface AuthContextValue {
  user: CommonsUser | null
  loading: boolean
  signOut: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  signOut: async () => {},
  refresh: async () => {},
})

/**
 * A2F-587 belt-and-suspenders: verify /auth/me returned a user whose
 * active org is the public-signup org. The backend middleware is the
 * real enforcement (it 403s commons-surface requests from users without
 * CTDCommons membership), but if a misconfig ever lets a non-CTDCommons
 * org through, we catch it here.
 */

function isCommonsMember(user: CommonsUser): boolean {
  const active = user.orgs.find((o) => o.orgId === user.orgId)
  return Boolean(active?.isPublicSignup)
}

/**
 * A2F-633 (architectural): write the flash the LoginPage will surface,
 * but do NOT call `supabase.auth.signOut()`.
 *
 * The prior implementation bundled two responsibilities: (a) mark that
 * this surface has denied the user so LoginPage can show a flash, and
 * (b) mutate the Supabase session lifecycle. (b) is wrong inside an
 * `/auth/me` error handler — for every transient-session flow that
 * arrives with a temporary session before the commons membership
 * resolves (password recovery on `/auth/new-password`, magic-link OTP
 * verification on `/auth/verified`, future OAuth / onboarding flows),
 * signing out here destroys the session before the downstream page
 * (UpdatePasswordPage, etc.) can use it.
 *
 * Callers that need React user state cleared should `setUser(null)`
 * themselves — keeps session lifecycle and React state orthogonal.
 * If the user ends up on `/auth/login` with a stale Supabase session
 * still alive, LoginPage's existing flash-read handles the UX.
 *
 * The `reason` is captured in PostHog via `Telemetry.errorSwallowed`
 * at each call site so we can tell which deny path fired without
 * re-deriving it from callstacks.
 */
function denyCommonsSession(reason: string): void {
  sessionStorage.setItem(COMMONS_AUTH_ERROR_KEY, COMMONS_AUTH_ERROR_MESSAGE)
  Telemetry.errorSwallowed({
    error_type: 'commons_access_denied',
    operation: reason,
    screen_name: typeof window !== 'undefined' ? window.location.pathname : '',
    error_message: COMMONS_AUTH_ERROR_MESSAGE,
  })
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CommonsUser | null>(null)
  const [loading, setLoading] = useState(true)
  const lastFetchedRef = useRef<string | null>(null)

  const fetchMe = useCallback(async (): Promise<CommonsUser | null> => {
    try {
      const me = await api.auth.me()
      return me ?? null
    } catch (err) {
      // A2F-587: the tenant middleware returns a 403 with
      // {error: 'commons_access_denied'} when a signed-in user lacks
      // CTDCommons membership. `apiFetch` surfaces that as a typed
      // CommonsDeniedError (A2F-636) so this branch no longer depends
      // on the raw response message format. A2F-633 (architectural):
      // deny no longer signs out — session is preserved so transient
      // onboarding sessions (OTP, recovery) can complete downstream
      // work. React state is cleared by the caller.
      if (err instanceof CommonsDeniedError) {
        denyCommonsSession('fetch_me_denied')
      }
      return null
    }
  }, [])

  // Restore session on mount.
  useEffect(() => {
    // A2F-633 (architectural): denyCommonsSession no longer signs out,
    // so /auth/me 403s are non-destructive. These pathname guards are
    // now optimizations (skip a pointless network round-trip during a
    // flow where user state isn't needed yet), not correctness. Keep
    // them to avoid writing a spurious "commons access denied" flash
    // during the happy-path password-reset flow — the reset link is
    // a transient session, not the user's steady state.
    if (window.location.pathname === '/auth/new-password') {
      setLoading(false)
      return
    }

    let cancelled = false
    const init = async () => {
      try {
        const { data } = await supabase.auth.getSession()
        if (!cancelled && data.session) {
          const me = await fetchMe()
          if (!cancelled && me) {
            if (!isCommonsMember(me)) {
              denyCommonsSession('init_not_commons_member')
              setUser(null)
              return
            }
            setUser(me)
            lastFetchedRef.current = data.session.user.id
            // onAuthStateChange does not fire SIGNED_IN when a
            // persisted session is restored on page load — only on
            // fresh logins — so identify explicitly here too.
            Telemetry.identify(me.id, { org_id: me.orgId ?? 'none' })
          }
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    init()
    return () => {
      cancelled = true
    }
  }, [fetchMe])

  // React to login / logout events.
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // A2F-633 (architectural): deny no longer signs out, so these
      // pathname-gated early returns are optimizations, not required
      // for correctness. On /auth/new-password the user's transient
      // recovery session shouldn't drive user-state hydration — the
      // page handles its own auth flow via explicit setSession +
      // updateUser. Same for OTP verification on /auth/verified below.
      if (event === 'PASSWORD_RECOVERY') {
        setLoading(false)
        return
      }
      if (
        event === 'SIGNED_IN' &&
        window.location.pathname === '/auth/new-password'
      ) {
        return
      }

      if (event === 'SIGNED_IN' && session) {
        // Avoid a duplicate /me call if we already resolved this user
        if (lastFetchedRef.current === session.user.id && user) return
        const me = await fetchMe()
        if (me) {
          if (!isCommonsMember(me)) {
            denyCommonsSession('signed_in_not_commons_member')
            setUser(null)
            return
          }
          setUser(me)
          lastFetchedRef.current = session.user.id
          // Pass org_id as a PostHog person property so analysts can
          // filter a user's full event history by tenant without
          // joining on individual event props.
          Telemetry.identify(me.id, { org_id: me.orgId ?? 'none' })
        } else {
          // fetchMe returned null — either /auth/me 403'd (deny flash
          // already written inside fetchMe) or other error. Clear React
          // state so ProtectedRoute redirects. Supabase session
          // preserved per A2F-633 architectural fix.
          setUser(null)
        }
      } else if (event === 'SIGNED_OUT') {
        setUser(null)
        lastFetchedRef.current = null
      }
    })
    return () => subscription.unsubscribe()
  }, [fetchMe, user])

  const signOut = useCallback(async () => {
    const durationSeconds = getSessionDurationSeconds()
    Telemetry.userSignedOut({ session_duration_seconds: durationSeconds })
    Telemetry.sessionEnded({ session_duration_seconds: durationSeconds })
    Telemetry.reset()
    await supabase.auth.signOut()
    setUser(null)
  }, [])

  const refresh = useCallback(async () => {
    lastFetchedRef.current = null
    const me = await fetchMe()
    if (!me) {
      // Either denied (flash written inside fetchMe) or other error —
      // clear React state. Supabase session intact per A2F-633.
      setUser(null)
      return
    }
    if (!isCommonsMember(me)) {
      denyCommonsSession('refresh_not_commons_member')
      setUser(null)
      return
    }
    setUser(me)
  }, [fetchMe])

  return (
    <AuthContext.Provider value={{ user, loading, signOut, refresh }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
